import { useCallback } from 'react';

import type {
	LuminaSourceClickEvent,
	LuminaSourceClickHandler,
} from '@atlassian/lumina/on-source-click';

import type { AIAnswerContextType } from '../../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerCopyButtonClicked } from '../../../../ai-answer-dialog/on-ai-answer-copy-button-clicked';
import { onAIAnswerSourceClick } from '../../../../ai-answer-dialog/on-ai-answer-source-click';
import type { SourceSmartCardType } from '../../../../ai-answer-dialog/types';
import { SainAction } from '../../../../common/controller/analytics/hooks/sain-action';
import { useSAINUsageCriteriaMet } from '../../../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../../../common/controller/analytics/use-analytics';
import { SAINFeatureControl } from '../../../../common/utils/sain-feature-control';

/**
 * Fires `aiAnswerSourceLink clicked` for inline-Lumina source surfaces
 * (`<link-list>` rows, `<smart-link>` `LinkEmbed`) rendered in a SAIN answer.
 */
export const useLuminaSourceClickAnalytics = ({
	aiAnswerDialogContext,
}: {
	aiAnswerDialogContext: AIAnswerContextType;
}): LuminaSourceClickHandler => {
	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
	const [fireAnalyticsEvent] = useAnalytics();

	const commonAttributes = aiAnswerDialogContext?.commonAttributes;
	const onAIAnswerSourceClickCallback = aiAnswerDialogContext?.callbacks?.onAIAnswerSourceClick;

	return useCallback<LuminaSourceClickHandler>(
		(event: LuminaSourceClickEvent) => {
			if (!commonAttributes) {
				if (process.env.NODE_ENV !== 'production') {
					throw new Error(
						'useLuminaSourceClickAnalytics: missing AIAnswerContext.commonAttributes.',
					);
				}
				return;
			}

			const { url, componentType, indexInContainer, isAuxClick, isContextMenu, isCopy } = event;

			if ((isAuxClick || isContextMenu) && !SAINFeatureControl.isNonPrimaryClickTrackingEnabled()) {
				return;
			}

			// Copy interactions reuse the existing `aiAnswerCopy clicked` event.
			if (isCopy) {
				fireAnalyticsEvent(
					onAIAnswerCopyButtonClicked(commonAttributes, 'inline-lumina-link-embed'),
				);

				fireSAINUsageEvent({
					metCriteria: SainAction.COPY_CLICKED,
					commonAttributes,
				});

				onAIAnswerSourceClickCallback?.();
				return;
			}

			const sourceCardType: SourceSmartCardType | undefined =
				componentType === 'linkList'
					? 'inline-lumina-link-list'
					: componentType === 'linkEmbed'
						? 'inline-lumina-link-embed'
						: undefined;

			let hostname: string | undefined;
			try {
				hostname = new URL(url).hostname;
			} catch {
				hostname = undefined;
			}

			const sourceProduct = commonAttributes.sourceProduct ?? '';

			fireAnalyticsEvent(
				onAIAnswerSourceClick({
					// Spread first so per-click fields below override context values.
					...commonAttributes,
					sourceIndex: indexInContainer,
					hostname,
					isAuxClick,
					isContextMenu,
					sourceCardType,
					sourceProduct,
					isCitation: false,
				}),
			);

			fireSAINUsageEvent({
				metCriteria: SainAction.SOURCE_CLICKED,
				commonAttributes,
				sourceProduct,
			});

			onAIAnswerSourceClickCallback?.();
		},
		[fireAnalyticsEvent, commonAttributes, fireSAINUsageEvent, onAIAnswerSourceClickCallback],
	);
};
